import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listStockBalances } from '../../api/inventory';
import { listItems } from '../../api/masterData';
import { listWarehouses } from '../../api/orgData';
import { useBranch } from '../../context/BranchContext';
import {
  ClickableRow,
  EmptyState,
  ErrorNote,
  ListToolbar,
  LoadingNote,
  Pagination,
  PageHeader,
  Select,
  SummaryStrip,
  formatDateTime,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
} from '../../components/common';
import type { Item, StockBalance, Warehouse } from '../../types/domain';

const LOW_STOCK_THRESHOLD = 5;

/**
 * Stock Balance — read-only view of the trigger-maintained on-hand
 * projection (migration 0008). Never written to directly; always a
 * consequence of stock_ledger rows. Clicking an item's balance row
 * navigates to Stock Ledger pre-filtered to that item — a real drilldown
 * into the movements that produced this balance, not a static number.
 *
 * FIXED: "Terakhir Diperbarui" previously rendered "Invalid Date" whenever
 * updated_at was missing/unparseable (surfaced by an incomplete QA harness
 * fixture, not the real schema — updated_at is a real NOT NULL trigger-
 * maintained column, migration 0008) — now uses the shared formatDateTime
 * helper, which renders "—" instead of ever showing "Invalid Date".
 *
 * FIXED: the same item can legitimately appear more than once here — once
 * per warehouse it has stock in (qty_on_hand is a (warehouse_id, item_id)
 * projection, migration 0008) — which read as ambiguous duplicate rows
 * without a visible Warehouse column. Added one, resolved from the real
 * listWarehouses() the same way Transfers/Waste already do; balances are
 * never aggregated across warehouses (no canonical business rule asks for
 * that), each row stays exactly what stock_balances actually stores.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 */
export default function StockBalancePage() {
  const navigate = useNavigate();
  const { activeBranchId, activeBranchWarehouseIds } = useBranch();
  const [balances, setBalances] = useState<StockBalance[]>([]);
  const [itemsById, setItemsById] = useState<Record<string, Item>>({});
  const [warehousesById, setWarehousesById] = useState<Record<string, Warehouse>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('ALL');
  const [warehouseFilter, setWarehouseFilter] = useState('ALL');

  // stock_balances has no branch_id (only warehouse_id) — scoped via the
  // active branch's warehouse ids, re-fetching whenever the branch changes.
  useEffect(() => {
    setLoading(true);
    Promise.all([listStockBalances(undefined, activeBranchId ? activeBranchWarehouseIds : undefined), listItems(), listWarehouses()])
      .then(([balanceRows, itemRows, warehouseRows]) => {
        setBalances(balanceRows);
        setItemsById(Object.fromEntries(itemRows.map((it) => [it.item_id, it])));
        setWarehousesById(Object.fromEntries(warehouseRows.map((w) => [w.warehouse_id, w])));
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId, activeBranchWarehouseIds.join(',')]);

  const warehouseOptions = useMemo(
    () => Array.from(new Set(balances.map((b) => b.warehouse_id))).map((id) => ({ warehouse_id: id, name: warehousesById[id]?.name ?? id })),
    [balances, warehousesById]
  );

  const stockState = (qty: number) => (qty <= 0 ? 'ZERO' : qty <= LOW_STOCK_THRESHOLD ? 'LOW' : 'OK');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return balances.filter((b) => {
      const item = itemsById[b.item_id];
      if (q && !(item?.item_code.toLowerCase().includes(q) || item?.name.toLowerCase().includes(q) || b.item_id.toLowerCase().includes(q))) return false;
      if (stateFilter !== 'ALL' && stockState(b.qty_on_hand) !== stateFilter) return false;
      if (warehouseFilter !== 'ALL' && b.warehouse_id !== warehouseFilter) return false;
      return true;
    });
  }, [balances, itemsById, search, stateFilter, warehouseFilter]);

  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 20);
  const zeroCount = balances.filter((b) => b.qty_on_hand <= 0).length;
  const lowCount = balances.filter((b) => b.qty_on_hand > 0 && b.qty_on_hand <= LOW_STOCK_THRESHOLD).length;

  return (
    <div>
      <PageHeader title="Stock Balance" subtitle="Saldo stok per gudang — proyeksi trigger-maintained dari Stock Ledger — migration 0008." />
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={[
              { label: 'SKU dengan Saldo', value: balances.length },
              { label: 'Stok Habis', value: zeroCount, tone: zeroCount > 0 ? 'danger' : 'success' },
              { label: `Stok Rendah (≤${LOW_STOCK_THRESHOLD})`, value: lowCount, tone: lowCount > 0 ? 'warning' : 'success' },
            ]}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari item..."
            resultCount={total}
            filters={
              <>
                <Select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} style={{ width: 160 }}>
                  <option value="ALL">Semua kondisi</option>
                  <option value="OK">Normal</option>
                  <option value="LOW">Stok rendah</option>
                  <option value="ZERO">Stok habis</option>
                </Select>
                {warehouseOptions.length > 1 && (
                  <Select value={warehouseFilter} onChange={(e) => setWarehouseFilter(e.target.value)} style={{ width: 180 }}>
                    <option value="ALL">Semua gudang</option>
                    {warehouseOptions.map((w) => (
                      <option key={w.warehouse_id} value={w.warehouse_id}>
                        {w.name}
                      </option>
                    ))}
                  </Select>
                )}
              </>
            }
          />
          {balances.length === 0 && <EmptyState text="Belum ada saldo stok." />}
          {balances.length > 0 && total === 0 && <EmptyState text="Tidak ada saldo yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Item</th>
                  <th style={thStyle}>Gudang</th>
                  <th style={thStyle}>Qty on hand</th>
                  <th style={thStyle}>Kondisi</th>
                  <th style={thStyle}>Terakhir diperbarui</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((b) => {
                  const state = stockState(b.qty_on_hand);
                  return (
                    <ClickableRow
                      key={`${b.warehouse_id}-${b.item_id}`}
                      onClick={() => navigate(`/app/stock-ledger?item=${b.item_id}&warehouse=${b.warehouse_id}`)}
                    >
                      <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{itemsById[b.item_id]?.item_code ?? b.item_id}</td>
                      <td style={tdStyle}>{warehousesById[b.warehouse_id]?.name ?? b.warehouse_id}</td>
                      <td style={tdStyle}>{b.qty_on_hand}</td>
                      <td style={{ ...tdStyle, color: state === 'ZERO' ? 'var(--alex-danger)' : state === 'LOW' ? '#92620A' : 'var(--alex-success)', fontWeight: 600 }}>
                        {state === 'ZERO' ? 'Habis' : state === 'LOW' ? 'Rendah' : 'Normal'}
                      </td>
                      <td style={tdStyle}>{formatDateTime(b.updated_at)}</td>
                    </ClickableRow>
                  );
                })}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}
    </div>
  );
}
